import React, { useMemo } from 'react';

import {
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  makeStyles,
  alpha,
  Box,
  Tooltip,
  Chip,
} from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';

import { useAppSelector } from 'store';
import { mensagensSelector, TiposMensagemEnum } from 'store/reducers/mensagens';
import {
  TemplateInterface,
  WppCategoryEnumLabels,
} from 'store/reducers/templates';

const useStyles = makeStyles(theme => ({
  scrollContainer: {
    scrollbarColor: `${theme.palette.background.tertiary} ${theme.palette.background.paper}`,
    paddingTop: theme.spacing(1),
  },
  listRoot: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  listItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: 10,

    '&.Mui-selected': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      borderColor: alpha(theme.palette.primary.main, 0.2),

      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.2),
      },
    },

    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },

    '&:hover $chip': {
      backgroundColor: alpha(theme.palette.grey[900], 0.1),
      border: `1px solid ${alpha(theme.palette.grey[900], 0.2)}`,
    },

    '&.Mui-selected $chip': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      color: theme.palette.primary.main,
    },

    '&.Mui-selected:hover $chip': {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    },
  },
  chip: {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${alpha(theme.palette.grey[900], 0.1)}`,
    color: theme.palette.getContrastText(theme.palette.background.default),
    fontSize: 10,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  endMessage: {
    textAlign: 'center',
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  tooltipText: {
    fontSize: theme.typography.pxToRem(12),
  },
}));

interface TemplateListProps {
  templates: TemplateInterface[];
  selectedTemplate: TemplateInterface | null;
  onTemplateSelect: (template: TemplateInterface) => void;
  loading?: boolean;
}

export const TemplateList: React.FC<TemplateListProps> = ({
  templates,
  selectedTemplate,
  onTemplateSelect,
  loading,
}) => {
  const classes = useStyles();
  const { mensagensAtendimento } = useAppSelector(mensagensSelector);

  const lastSentTemplateId = useMemo(() => {
    const lastMessage = mensagensAtendimento.find(
      msg => msg.tipo !== TiposMensagemEnum.EVENTO,
    );

    if (
      lastMessage?.tipo === TiposMensagemEnum.TEMPLATE_WHATSAPP &&
      lastMessage?.whatsappTemplateData?.id
    ) {
      return lastMessage.whatsappTemplateData.id;
    }

    return null;
  }, [mensagensAtendimento]);

  return (
    <Box className={classes.scrollContainer}>
      <List className={classes.listRoot}>
        {templates.map(template => {
          const isLastSent = lastSentTemplateId === template.id;

          return (
            <ListItem
              key={template.id}
              button
              selected={selectedTemplate?.id === template.id}
              onClick={() => onTemplateSelect(template)}
              className={classes.listItem}
            >
              <ListItemText
                primary={template.wppName}
                primaryTypographyProps={{
                  style: { fontSize: 14 },
                }}
              />

              <Chip
                size="small"
                label={WppCategoryEnumLabels[template.wppCategory]}
                className={classes.chip}
              />

              {isLastSent && (
                <Tooltip
                  title="Este template foi enviado na última mensagem ao contato"
                  classes={{ tooltip: classes.tooltipText }}
                >
                  <InfoOutlined
                    color="primary"
                    fontSize="small"
                    style={{ marginLeft: 6, height: 16, width: 16 }}
                  />
                </Tooltip>
              )}
            </ListItem>
          );
        })}
      </List>

      {loading && (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && templates.length === 0 && (
        <Box className={classes.endMessage}>Nenhum template encontrado</Box>
      )}
    </Box>
  );
};
